import { observable, reaction, action } from 'mobx';
import { ZipApi } from '../../lib/ZipApi';
import { directoryStore } from './../DirectoryStore';
import { OptimizelyProduct } from '../../types/DataSyncItem';
import { HubApp } from '../../types/DataSyncItem';
import { Maybe } from '../../utils/maybe';
import ProductStoreFactory from './ProductStoreFactory';

export interface Product {
  text: string;
  value: string;
  iconPath?: string;
}

export interface ProductInstance {
  text: string;
  value: string;
  iconPath?: string;
  unavailable?: boolean;
}

export abstract class DataSyncProductStore {
  @observable protected _hubAppsLoading = false;
  @observable protected _hubApps: HubApp[] = [];

  constructor() {
    reaction(() => directoryStore.trackerId, () => {
      this.fetchHubApps();
    }, {fireImmediately: true});
  }

  public get productInstancesLoading(): boolean {
    return ProductStoreFactory.getProductInstancesStore().productInstancesLoading;
  }

  public abstract getProductList(): Product[];

  public getInstanceList(
    productName: OptimizelyProduct,
    configuredInstanceId?: string,
    configuredInstanceName?: string
  ): ProductInstance[] {
    let instances: ProductInstance[] = [];

    if (productName === OptimizelyProduct.HUB_APP) {
      instances = this._hubApps.map((app) => {
        return {
          text: app.name,
          value: app.app_id,
          iconPath: app.icon_url
        };
      });
    } else {
      const productInstances = ProductStoreFactory.getProductInstancesStore().productInstances;
      const product = productInstances?.find((obj) => obj.product === productName);
      if (!product) {
        instances = [];
      } else {
        instances = product.instances.map((instance) => {
          return {
            text: instance.name,
            value: instance.id,
          };
        });
      }
    }

    // If configured instance is provided and not in available list, prepend it as unavailable
    if (configuredInstanceId && configuredInstanceName) {
      const isAvailable = instances.some((inst) => inst.value === configuredInstanceId);
      if (!isAvailable) {
        instances.unshift({
          text: configuredInstanceName,
          value: configuredInstanceId,
          unavailable: true,
          // Use default icon for unavailable Hub Apps
          iconPath: productName === OptimizelyProduct.HUB_APP ? '/assets/app_icon.svg' : undefined,
        });
      }
    }

    return instances;
  }

  public get hubAppsLoading(): boolean {
    return this._hubAppsLoading;
  }

  public getInstance(
    productName: OptimizelyProduct,
    instanceId: string
  ): ProductInstance | undefined {
    if (productName === OptimizelyProduct.HUB_APP) {
      return Maybe.of(this._hubApps)
        .find((apps) => apps.find((app) => app.app_id === instanceId))
        .map((app) => ({
          text: app.name,
          value: app.app_id,
          iconPath: app.icon_url,
        }))
        .unwrap();
    }

    const productInstances = ProductStoreFactory.getProductInstancesStore().productInstances;
    return Maybe.of(productInstances)
      .find((products) => products.find((p) => p.product === productName))
      .find((product) => product.instances.find((i) => i.id === instanceId))
      .map((instance) => ({
        text: instance.name,
        value: instance.id,
      }))
      .unwrap();
  }

  public isInstanceAvailable(
    productName: OptimizelyProduct,
    instanceId: string
  ): boolean {
    if (!instanceId) {
      return true;
    }

    if (productName === OptimizelyProduct.HUB_APP) {
      return this._hubApps.some((app) => app.app_id === instanceId);
    } else {
      return Maybe.of(ProductStoreFactory.getProductInstancesStore().productInstances)
        .find((products) => products.find((p) => p.product === productName))
        .map((product) => product.instances.some((instance) => instance.id === instanceId))
        .unwrapOr(false);
    }
  }

  protected abstract getAppFeatureType(): string;

  @action
  private async fetchHubApps() {
    if (!directoryStore.trackerId) return;

    try {
      this._hubApps = [];
      this._hubAppsLoading = true;
      const appInstallationsResponse = await ZipApi.listAppInstalls(directoryStore.trackerId, this.getAppFeatureType());

      // Use Promise.all to wait for all app version fetches to complete
      if (appInstallationsResponse.installations) {
        const hubAppPromises = appInstallationsResponse.installations.map(async (installation) => {
          const appResponse = await ZipApi.fetchAppVersion(installation.app_id, installation.version);
          return {
            name: appResponse.app_version.metadata.display_name,
            icon_url: appResponse.app_version.metadata.icon_url,
            app_id: installation.app_id,
            version: installation.version
          } as HubApp;
        });

        this._hubApps = await Promise.all(hubAppPromises);
      }
    } finally {
      this._hubAppsLoading = false;
    }
  }
}
